"use client";
import React, { Suspense, useEffect, useMemo } from "react";
import SuggestionCard from "./SuggestionCard";
import AccountCard from "../../../components/common/AccountCard/AccountCard";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import { User, Role } from "@/gql/graphql";
import SkeletonSuggestion from "./SkeletonSuggestion";
import { useSearchContext } from "@/contexts/SearchContext";
import useLang from "@/hooks/useLang";
import { useAppSelector } from "@/lib/store/hooks";
import { USER_INFOS } from "@/graphql/user";
import useImage from "@/hooks/useImage";

export const GET_CRAFTMEN = gql(`
  query UsersSuggestion(
    $where: UserWhereInput
    $orderBy: [UserOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
  ) {
    users(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
      id
      lastName
      name
      workshopName
      role
      minQuantityOrder
      country {
        name_fr
      }
      PhotoAchievements {
        id
        url
        name
      }
      ProfilePhoto {
        id
        url
        name
      }
      Categories {
        id
        name_en
        name_fr
      } 
      UserStories {
        id
      }   
    }
  }
`);

const SuggestionsList = () => {
  const { orderBy } = useSearchContext();
  const { getText } = useLang();
  const { getUri } = useImage();

  const user = useAppSelector((state) => state.customer.user);
  const username = `${user?.name} ${user?.lastName}`;

  const { data: dataCraftmen, loading: loadingCraftmen } = useQuery<{
    users: User[];
  }>(GET_CRAFTMEN, {
    variables: {
      where: {
        role: {
          equals: Role.Craftman,
        },
      },
      take: 50,
      ...orderBy,
    },
  });

  const userId = useAppSelector((state) => state.customer.user?.id);

  const [getUserInfos, { data }] = useLazyQuery<{ user: User }>(USER_INFOS);

  useEffect(() => {
    if (userId) {
      getUserInfos({
        variables: {
          where: {
            id: userId,
          },
        },
      });
    }
  }, [getUserInfos, userId]);

  const profilePhoto = getUri(data?.user?.ProfilePhoto?.url);

  // data of 5 suggest craftmens
  const suggestCraftmens = useMemo(() => {
    if (!dataCraftmen?.users) return [];
    return [...dataCraftmen.users]
      .filter((user) => user?.PhotoAchievements?.length > 0)
      .sort(() => Math.random() - 0.5) // Shuffle array
      .slice(0, 5); // Take first 5 elements
  }, [dataCraftmen?.users]);

  return (
    <>
      {loadingCraftmen && <SkeletonSuggestion />}
      {suggestCraftmens && !loadingCraftmen && (
        <div className="hidden xl:block lg:w-[25.8vw] bg-white space-y-7 py-10 pl:0 lg:pl-5 pr-9 text-sm">
          <AccountCard username={username} image={profilePhoto} />

          <div className="space-y-5">
            <div className="flex justify-between">
              <span className="text-zinc-500 font-semibold">
                {getText({ id: "suggestsText" })}
              </span>
              <span className="font-semibold">
                {getText({ id: "see_all" })}
              </span>
            </div>

            <Suspense fallback={<SkeletonSuggestion />}>
              {suggestCraftmens.map((craftman) => (
                <SuggestionCard key={craftman.id} user={craftman} />
              ))}
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
};

export default SuggestionsList;
